import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import { Archive, Download, FileText, Lock, Loader2, FileSignature, Share2, Copy, X, Eye, ChevronDown, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const LODGE_ROLES = ["admin", "org_admin", "cro"];

/**
 * Pack Archive — iter107.
 *
 * Lists every immutable lodged board pack bundle for the tenant and lets
 * the eligible roles lodge a new one. Lodging captures the executive
 * pack, the Risk Operating Review and the Reference Appendix into a
 * single zip, snapshots the underlying data, and writes an audit-grade
 * row that cannot be edited or deleted via the UI (the retention sweep
 * is the only path that removes data, and it is soft-delete first).
 */
export default function PackArchive() {
  const { user } = useAuth();
  const canLodge = LODGE_ROLES.includes(user?.role);

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lodgeOpen, setLodgeOpen] = useState(false);
  const [lodging, setLodging] = useState(false);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  // Share-link modal state. We track the active pack id so the same
  // dialog component can be reused for any row.
  const [sharePack, setSharePack] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/exports/board-pack/archive");
      setPacks(data || []);
    } catch {
      toast.error("Failed to load archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const submitLodge = async (e) => {
    e.preventDefault();
    setLodging(true);
    try {
      await api.post("/exports/board-pack/lodge", {
        as_of: new Date().toISOString(),
        label: label || `Board pack — ${new Date().toISOString().slice(0, 10)}`,
        notes,
      });
      toast.success("Pack lodged. This record is now immutable.");
      setLodgeOpen(false);
      setLabel("");
      setNotes("");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lodge failed");
    } finally {
      setLodging(false);
    }
  };

  const download = async (packId, label) => {
    try {
      const res = await api.get(`/exports/board-pack/archive/${packId}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label.replace(/[^\w-]+/g, "-")}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Download failed");
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Pack archive" description="Immutable archive of lodged board pack bundles." path="/pack-archive" />
      <Navbar />

      <main className="mx-auto max-w-6xl px-6 py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4" data-testid="pack-archive-header">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.18em] text-ek-ink/60">Governance · audit trail</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-ek-ink">Board pack archive</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ek-ink/70">
              Every lodged board pack is an immutable, hashed record — executive pack, Risk Operating Review and Reference Appendix bundled into one zip with a manifest. Use this archive to answer &ldquo;show me what was in the May board pack&rdquo; without rebuilding from drifting weekly snapshots.
            </p>
          </div>
          {canLodge && (
            <button
              onClick={() => setLodgeOpen(true)}
              data-testid="lodge-pack-button"
              className="ek-btn flex items-center gap-2"
            >
              <FileSignature size={16} /> Lodge new pack
            </button>
          )}
        </header>

        {!canLodge && (
          <div className="mb-6 flex items-start gap-3 rounded-md border border-ek-ink/10 bg-white p-4 text-sm" data-testid="pack-archive-readonly-note">
            <Lock size={16} className="mt-0.5 shrink-0 text-ek-ink/50" />
            <p className="text-ek-ink/70">
              You can read and download any lodged pack. Lodging a new pack requires the <b>admin</b>, <b>org_admin</b> or <b>CRO</b> role (the CRO is the natural accountable lodger when presenting to the board).
            </p>
          </div>
        )}

        <section className="rounded-md border border-ek-ink/10 bg-white">
          {loading ? (
            <div className="flex items-center justify-center p-10 text-ek-ink/50" data-testid="pack-archive-loading">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : packs.length === 0 ? (
            <div className="p-10 text-center" data-testid="pack-archive-empty">
              <Archive className="mx-auto mb-3 text-ek-ink/30" size={36} />
              <p className="text-sm font-medium text-ek-ink/70">No lodged packs yet</p>
              <p className="mt-1 text-xs text-ek-ink/50">
                {canLodge
                  ? <>Click <b>Lodge new pack</b> above to capture an immutable record of the current board pack bundle.</>
                  : "Once an admin or CRO lodges the first pack, it will appear here."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="pack-archive-table">
              <thead className="border-b border-ek-ink/10 text-xs font-semibold uppercase tracking-wider text-ek-ink/55">
                <tr>
                  <th className="px-5 py-3 text-left">Label · Period</th>
                  <th className="px-5 py-3 text-left">Lodged</th>
                  <th className="px-5 py-3 text-left">Lodged by</th>
                  <th className="px-5 py-3 text-left">Contents</th>
                  <th className="px-5 py-3 text-left">SHA-256</th>
                  <th className="px-5 py-3 text-left">Size</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {packs.map((p) => (
                  <PackRow
                    key={p.pack_id}
                    pack={p}
                    canLodge={canLodge}
                    onShare={() => setSharePack(p)}
                    onDownload={() => download(p.pack_id, p.label)}
                    fmtBytes={fmtBytes}
                  />
                ))}
              </tbody>
            </table>
          )}
        </section>

        <p className="mt-6 text-xs text-ek-ink/50" data-testid="pack-archive-retention-note">
          Retention default: 7 years (statutory board-record window). Soft-deletes are recoverable for 30 days before
          hard-purge. Configure per-tenant retention via <code className="rounded bg-ek-canvas px-1.5 py-0.5">tenant_settings.pack_archive_retention_days</code>.
        </p>
      </main>

      <Dialog open={lodgeOpen} onOpenChange={setLodgeOpen}>
        <DialogContent data-testid="lodge-pack-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Lodge board pack</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              Once lodged, the bundle is <b>immutable</b>. The system snapshots the executive pack, the Risk Operating
              Review and the Reference Appendix into a single hashed zip, plus a frozen copy of the underlying risks,
              briefings and alerts. Future &ldquo;vs last pack&rdquo; deltas will compute against this snapshot.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLodge} className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-ek-ink/65">
                Label (optional)
              </label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="lodge-pack-label-input"
                placeholder="e.g. June 2026 Board · Q2 close"
                className="mt-1 w-full rounded border border-ek-ink/15 bg-white px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-ek-ink/65">
                Notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="lodge-pack-notes-input"
                placeholder="Anything notable — version, attendees, post-meeting actions…"
                className="mt-1 w-full rounded border border-ek-ink/15 bg-white px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setLodgeOpen(false)}
                className="rounded border border-ek-ink/15 px-4 py-2 text-sm text-ek-ink/70"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={lodging}
                data-testid="lodge-pack-submit"
                className="ek-btn flex items-center gap-2 disabled:opacity-50"
              >
                {lodging ? <Loader2 className="animate-spin" size={14} /> : <FileSignature size={14} />}
                {lodging ? "Lodging…" : "Lodge"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ShareLinkDialog pack={sharePack} onClose={() => setSharePack(null)} />
    </div>
  );
}

/**
 * PackRow — single archive row + expandable view-log subrow.
 *
 * The view log surfaces "who actually opened the pack" rolled up across
 * every share link, with hashed-IP unique-device counts and a recent-30
 * view timeline. Two weeks before a board meeting this lets the CRO
 * see at a glance who's read it and who needs chasing.
 */
function PackRow({ pack, canLodge, onShare, onDownload, fmtBytes }) {
  const p = pack;
  const [expanded, setExpanded] = useState(false);
  const [log, setLog] = useState(null);
  const [logLoading, setLogLoading] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    if (log) return; // lazy: only fetch once per expansion
    setLogLoading(true);
    api.get(`/exports/board-pack/archive/${p.pack_id}/view-log`)
      .then((r) => setLog(r.data))
      .catch(() => setLog({ error: true }))
      .finally(() => setLogLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, p.pack_id]);

  return (
    <>
      <tr className="border-b border-ek-ink/5 last:border-0" data-testid={`pack-row-${p.pack_id}`}>
        <td className="px-5 py-3">
          <p className="font-medium text-ek-ink">{p.label}</p>
          <p className="text-xs text-ek-ink/55">As of {(p.as_of || "").slice(0, 16).replace("T", " ")}</p>
        </td>
        <td className="px-5 py-3 text-ek-ink/75">{(p.lodged_at || "").slice(0, 16).replace("T", " ")}</td>
        <td className="px-5 py-3 text-ek-ink/75">{p.lodged_by_email}</td>
        <td className="px-5 py-3 text-ek-ink/75">
          <span className="inline-flex items-center gap-1 text-xs">
            <FileText size={12} /> {p.risk_count} risks · {p.approved_briefings} briefings · {p.alerts} alerts
          </span>
        </td>
        <td className="px-5 py-3 font-mono text-xs text-ek-ink/55" title={p.sha256}>
          {(p.sha256 || "").slice(0, 12)}…
        </td>
        <td className="px-5 py-3 text-ek-ink/65">{fmtBytes(p.byte_size)}</td>
        <td className="px-5 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            {canLodge && (
              <>
                <button
                  onClick={() => setExpanded((v) => !v)}
                  data-testid={`pack-view-log-${p.pack_id}`}
                  title={expanded ? "Hide view log" : "Who opened this pack?"}
                  className="inline-flex items-center gap-1 rounded border border-ek-ink/15 px-2 py-1.5 text-xs font-medium text-ek-ink/70 hover:border-ek-purple/40 hover:text-ek-purple"
                >
                  {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <Eye size={12} />
                </button>
                <button
                  onClick={onShare}
                  data-testid={`pack-share-${p.pack_id}`}
                  title="Mint a time-limited share link"
                  className="inline-flex items-center gap-1 rounded border border-ek-purple/40 px-3 py-1.5 text-xs font-medium text-ek-purple hover:bg-ek-purple/5"
                >
                  <Share2 size={12} /> Share
                </button>
              </>
            )}
            <button
              onClick={onDownload}
              data-testid={`pack-download-${p.pack_id}`}
              className="inline-flex items-center gap-1 rounded border border-ek-ink/15 px-3 py-1.5 text-xs font-medium text-ek-ink hover:bg-ek-canvas"
            >
              <Download size={12} /> Download zip
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`pack-view-log-row-${p.pack_id}`}>
          <td colSpan={7} className="border-b border-ek-ink/5 bg-ek-canvas/60 px-5 py-4">
            <ViewLogPanel log={log} loading={logLoading} />
          </td>
        </tr>
      )}
    </>
  );
}

function ViewLogPanel({ log, loading }) {
  if (loading) return <p className="text-xs text-ek-ink/50">Loading view log…</p>;
  if (!log) return null;
  if (log.error) return <p className="text-xs text-rose-600">Failed to load view log</p>;
  if (log.link_count === 0) {
    return (
      <p className="text-xs text-ek-ink/55">
        No share links have been minted for this pack yet. Click <b>Share</b> above to distribute.
      </p>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="space-y-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">Activity</h4>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <dt className="text-ek-ink/55">Total views</dt>
          <dd className="text-right font-semibold text-ek-ink" data-testid="view-log-total">{log.view_count}</dd>
          <dt className="text-ek-ink/55">Unique devices</dt>
          <dd className="text-right font-semibold text-ek-ink">{log.unique_devices}</dd>
          <dt className="text-ek-ink/55">Active links</dt>
          <dd className="text-right text-ek-ink">{log.active_link_count} / {log.link_count}</dd>
          <dt className="text-ek-ink/55">Last opened</dt>
          <dd className="text-right text-ek-ink/80">
            {log.last_view_ts ? (log.last_view_ts || "").slice(0, 16).replace("T", " ") : "Never"}
          </dd>
        </dl>
      </div>
      <div className="md:col-span-2">
        <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ek-ink/55">
          Recent views (last 30)
        </h4>
        {log.recent_views?.length === 0 ? (
          <p className="text-xs text-ek-ink/50">
            Pack has share links but nobody has opened it yet — consider chasing recipients.
          </p>
        ) : (
          <div className="max-h-44 overflow-y-auto rounded border border-ek-ink/10 bg-white">
            <table className="w-full text-[11px]">
              <thead className="border-b border-ek-ink/10 text-[9px] uppercase tracking-wider text-ek-ink/55">
                <tr>
                  <th className="px-2 py-1 text-left">When</th>
                  <th className="px-2 py-1 text-left">Device (hashed)</th>
                  <th className="px-2 py-1 text-left">Via link</th>
                </tr>
              </thead>
              <tbody>
                {log.recent_views.map((v, i) => (
                  <tr key={i} className="border-b border-ek-ink/5 last:border-0">
                    <td className="px-2 py-1 font-mono text-ek-ink/70">{(v.ts || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-2 py-1 font-mono text-ek-ink/55">{(v.ip_hash || "").slice(0, 10) || "—"}</td>
                    <td className="px-2 py-1 text-ek-ink/65">{v.via_label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ShareLinkDialog — mint, list & revoke time-limited share links for a
 * single lodged pack. The token IS the auth on the public consume
 * endpoint, so the operator only needs the URL — no JWT, no recipient
 * pre-provisioning. Every view is audit-logged with hashed IP.
 */
function ShareLinkDialog({ pack, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [ttlHours, setTtlHours] = useState(72);
  const [label, setLabel] = useState("");

  const open = !!pack;
  const reload = async () => {
    if (!pack) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/exports/board-pack/archive/${pack.pack_id}/share-links`);
      setLinks(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load share links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setLabel("");
      setTtlHours(72);
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pack?.pack_id]);

  const createLink = async (e) => {
    e?.preventDefault?.();
    setCreating(true);
    try {
      const { data } = await api.post(
        `/exports/board-pack/archive/${pack.pack_id}/share-link`,
        { ttl_hours: ttlHours, label: label || null },
      );
      const url = `${window.location.origin}/api${data.url_path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link created and copied to clipboard.");
      } catch {
        toast.success("Share link created. Copy it from the list below.");
      }
      setLabel("");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create share link");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this share link? Anyone with the URL will get a 410 Gone next time they open it.")) {
      return;
    }
    try {
      await api.delete(`/exports/board-pack/archive/share-link/${token}`);
      toast.success("Share link revoked");
      await reload();
    } catch {
      toast.error("Failed to revoke share link");
    }
  };

  const copy = async (token) => {
    const url = `${window.location.origin}/api/share/board-pack/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Couldn't access the clipboard — copy manually from the URL bar.");
    }
  };

  const fmtExpiry = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    const hrs = (d - now) / (1000 * 60 * 60);
    if (hrs < 0) return "Expired";
    if (hrs < 24) return `In ${Math.round(hrs)}h`;
    return `In ${Math.round(hrs / 24)}d`;
  };

  const statusPill = (status) => {
    const styles = {
      active: "bg-emerald-100 text-emerald-800",
      expired: "bg-ek-ink/10 text-ek-ink/60",
      revoked: "bg-rose-100 text-rose-700",
    };
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${styles[status] || styles.expired}`}>
        {status}
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="share-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Share &ldquo;{pack?.label}&rdquo;
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            Mint a time-limited URL you can email to NEDs / the company secretary instead of
            attaching the zip. The token IS the auth — no ERIO account required to open the link.
            Every view is audit-logged (hashed IP + user-agent).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={createLink} className="grid gap-3 rounded-md border border-ek-ink/10 bg-ek-canvas p-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-ek-ink/65">
                Label (optional)
              </label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="share-link-label"
                placeholder="e.g. NED distribution Q2"
                className="mt-1 w-full rounded border border-ek-ink/15 bg-white px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-ek-ink/65">
                Expires in (hours, 1–336)
              </label>
              <input
                type="number"
                min={1}
                max={336}
                value={ttlHours}
                onChange={(e) => setTtlHours(Math.max(1, Math.min(336, Number(e.target.value) || 72)))}
                data-testid="share-link-ttl"
                className="mt-1 w-full rounded border border-ek-ink/15 bg-white px-3 py-2 text-sm focus:border-ek-purple focus:outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="share-link-create"
            className="ek-btn flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {creating ? <Loader2 className="animate-spin" size={14} /> : <Share2 size={14} />}
            {creating ? "Creating…" : "Create link & copy URL"}
          </button>
        </form>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ek-ink/65">
            Existing links {links.length > 0 && <span className="font-normal text-ek-ink/45">· {links.length}</span>}
          </h3>
          {loading ? (
            <p className="text-xs text-ek-ink/50">Loading…</p>
          ) : links.length === 0 ? (
            <p className="text-xs text-ek-ink/50" data-testid="share-link-empty">
              No share links yet. The first one you create will appear here.
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border border-ek-ink/10">
              <table className="w-full text-xs">
                <thead className="border-b border-ek-ink/10 bg-ek-canvas text-[10px] uppercase tracking-wider text-ek-ink/55">
                  <tr>
                    <th className="px-3 py-2 text-left">Label · Created</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Expires</th>
                    <th className="px-3 py-2 text-left">Views</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {links.map((lk) => (
                    <tr key={lk.token} className="border-b border-ek-ink/5 last:border-0"
                        data-testid={`share-link-row-${lk.token.slice(0, 8)}`}>
                      <td className="px-3 py-2">
                        <p className="text-ek-ink">{lk.label || <span className="text-ek-ink/45">No label</span>}</p>
                        <p className="text-[10px] text-ek-ink/50">
                          By {lk.created_by} · {(lk.created_at || "").slice(0, 16).replace("T", " ")}
                        </p>
                      </td>
                      <td className="px-3 py-2">{statusPill(lk.status)}</td>
                      <td className="px-3 py-2 text-ek-ink/70">{fmtExpiry(lk.expires_at)}</td>
                      <td className="px-3 py-2 text-ek-ink/70">
                        {lk.view_count} view{lk.view_count !== 1 ? "s" : ""}
                        {lk.unique_devices > 0 && (
                          <span className="ml-1 text-[10px] text-ek-ink/50">· {lk.unique_devices} device{lk.unique_devices !== 1 ? "s" : ""}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {lk.status === "active" && (
                            <>
                              <button
                                onClick={() => copy(lk.token)}
                                data-testid={`share-link-copy-${lk.token.slice(0, 8)}`}
                                title="Copy URL"
                                className="rounded p-1 text-ek-ink/65 hover:bg-ek-canvas hover:text-ek-purple"
                              >
                                <Copy size={12} />
                              </button>
                              <button
                                onClick={() => revoke(lk.token)}
                                data-testid={`share-link-revoke-${lk.token.slice(0, 8)}`}
                                title="Revoke this link"
                                className="rounded p-1 text-ek-ink/65 hover:bg-rose-50 hover:text-rose-700"
                              >
                                <X size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}
